<template>
  <f7-page>
    <f7-navbar :title="plan?.campaignName || $t('tontine.os')" back-link>
      <f7-nav-right>
        <f7-link icon-f7="info_circle" @click="openRules" />
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
    <f7-block v-if="loading" strong inset>
      <f7-preloader /> {{ $t('tontine.loading') }}
    </f7-block>

    <template v-else-if="plan">
      <!-- Status card -->
      <f7-block strong inset class="status-card">
        <div class="status-row">
          <f7-badge :class="statusClass">{{ statusLabel }}</f7-badge>
          <span class="mode-pill">{{ modeLabel }}</span>
        </div>
        <h2>{{ money(plan.contributionAmount, plan.currency) }} / {{ frequencyLabel }}</h2>
        <p>{{ $t('tontine.membersCount', { count: plan.memberCount }) }} · {{ $t('tontine.rotation', { method: allocationLabel }) }}</p>

        <template v-if="plan.currentCycle">
          <f7-progressbar :progress="plan.currentCycle.progressPercent || 0" color="green" />
          <div class="progress-meta">
            <span>{{ $t('tontine.confirmedOf', { confirmed: plan.currentCycle.confirmedMembers || 0, total: plan.currentCycle.memberCount || 0 }) }}</span>
            <span>{{ plan.currentCycle.progressPercent || 0 }}%</span>
          </div>
        </template>
        <f7-block v-else class="no-cycle">
          <f7-icon f7="square_grid_2x2" size="18" color="gray" />
          <span>{{ $t('tontine.noCycleYet') }}</span>
        </f7-block>
      </f7-block>

      <!-- Setup / activation warning -->
      <f7-block v-if="needsSetup || needsCycles" strong inset class="setup-warning">
        <f7-icon
          :f7="plan.status === 'PENDING_PARTNER_VERIFICATION' ? 'shield_checkmark' : 'exclamationmark_circle_fill'"
          :color="plan.status === 'PENDING_PARTNER_VERIFICATION' ? 'green' : 'orange'"
        />
        <div>
          <strong>{{ readinessTitle }}</strong>
          <p v-if="readinessMessage">{{ readinessMessage }}</p>
          <ul v-else-if="plan.readiness?.blockers?.length" class="blockers">
            <li v-for="(b, i) in plan.readiness.blockers" :key="i">{{ b }}</li>
          </ul>
        </div>
        <f7-button v-if="needsSetup" small fill round raised @click="continueSetup">{{ $t('tontine.continue') }}</f7-button>
        <f7-button v-else-if="needsCycles && plan.canManage" small fill round raised @click="generateCalendar">{{ $t('tontine.generateCalendar') }}</f7-button>
      </f7-block>

      <!-- Current cycle -->
      <template v-if="plan.currentCycle">
        <f7-block-title>{{ $t('tontine.currentCycle', { number: plan.currentCycle.cycleNumber }) }}</f7-block-title>
        <f7-list inset strong>
          <f7-list-item :title="$t('tontine.dueDate')" :after="formatDate(plan.currentCycle.dueDate)" />
          <f7-list-item :title="$t('tontine.payoutDate')" :after="formatDate(plan.currentCycle.payoutDate)" />
          <f7-list-item :title="$t('tontine.beneficiary')" :after="plan.currentCycle.beneficiaryDisplayNameSnapshot || $t('tontine.toDefine')" />
          <f7-list-item :title="$t('tontine.expectedTotal')" :after="money(plan.currentCycle.expectedTotal, plan.currency)" />
          <f7-list-item :title="$t('tontine.confirmedTotal')" :after="money(plan.currentCycle.confirmedTotal, plan.currency)" />
        </f7-list>

        <f7-block v-if="plan.myContribution" strong inset>
          <div class="my-contribution-title">{{ $t('tontine.myContribution') }}</div>
          <div class="my-contribution-amount">{{ money(plan.myContribution.expectedAmount, plan.currency) }}</div>
          <p :class="contributionStatusClass">
            {{ contributionStatusLabel }}
            <template v-if="plan.myContribution.status === 'RECEIPT_SUBMITTED' && plan.myContribution.confirmationSource === 'MEMBER_RECEIPT'">
              — {{ $t('tontine.pendingTreasurerConfirmation') }}
            </template>
          </p>
          <f7-button v-if="canPay" fill large @click="payContribution">{{ $t('tontine.payOrProvideProof') }}</f7-button>
          <f7-button v-else outline large @click="openContribution">{{ $t('tontine.viewMyContribution') }}</f7-button>
        </f7-block>

        <f7-block-title>{{ $t('tontine.actions') }}</f7-block-title>
        <f7-list inset>
          <f7-list-item :title="$t('tontine.viewCycle')" link @click="openCycle" />
          <f7-list-item :title="$t('tontine.rules')" link @click="openRules" />
          <f7-list-item :title="$t('tontine.ledger')" link @click="openLedger" />
          <f7-list-item v-if="hasPayouts" :title="$t('tontine.payouts')" link @click="openLedger" />
          <f7-list-item v-if="plan.canManage" :title="$t('tontine.manageContributions')" link @click="openCycle" />
          <f7-list-item v-if="plan.canManage" :title="$t('tontine.inviteMembers')" link @click="inviteMembers" />
        </f7-list>
      </template>

      <!-- Regulatory / pedagogical notice -->
      <f7-block strong inset class="info-notice">
        <p class="text-color-gray">
          <strong>{{ $t('tontine.infoNoticeTitle') }}</strong><br>
          {{ $t('tontine.infoNoticeBody') }}
        </p>
      </f7-block>
    </template>
    </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import { useI18n } from 'vue-i18n'
import { useTontineStore } from '@/js/pinia-stores/tontine'
import { useKeycloakStore } from '@/js/pinia-stores/keycloak'
import usePaymentTrigger from '@/js/composables/usePaymentTrigger'
import AppLayout from "../../components/common/AppLayout.vue";


const { t } = useI18n()
const props = defineProps({ campaignId: String, groupId: String })
const store = useTontineStore()
const keycloak = useKeycloakStore()
const payment = usePaymentTrigger()

const plan = computed(() => store.plan)
const loading = computed(() => store.loading)
const subject = computed(() => keycloak.subject)
const canPay = computed(() =>
  ['DUE', 'PAYMENT_REQUESTED', 'OVERDUE'].includes(plan.value?.myContribution?.status)
)
const needsSetup = computed(() => ['SETUP_REQUIRED', 'DRAFT'].includes(plan.value?.status))
const needsCycles = computed(() => plan.value?.status === 'ACTIVE' && !plan.value?.currentCycle)
const hasPayouts = computed(() => false)

const readability = computed(() => {
  const s = plan.value?.status
  return s === 'ACTIVE' ? 'green' : s === 'PENDING_PARTNER_VERIFICATION' ? 'green' : 'orange'
})
const readinessTitle = computed(() => {
  if (plan.value?.status === 'PENDING_PARTNER_VERIFICATION') return t('tontine.partnerVerificationTitle')
  if (needsCycles.value) return t('tontine.generateCalendarTitle')
  return t('tontine.setupRequiredTitle')
})
const readinessMessage = computed(() => {
  if (plan.value?.status === 'PENDING_PARTNER_VERIFICATION') return t('tontine.partnerVerificationBody')
  if (needsCycles.value) return t('tontine.generateCalendarBody')
  return ''
})

const statusClass = computed(() => {
  const s = plan.value?.status
  if (s === 'ACTIVE' || s === 'PENDING_PARTNER_VERIFICATION') return 'badge-green'
  if (s === 'COMPLETED') return 'badge-gray'
  return 'badge-orange'
})
const statusLabel = computed(() => {
  const s = plan.value?.status
  return (
    {
      ACTIVE: t('tontine.planStatus.ACTIVE'),
      PENDING_PARTNER_VERIFICATION: t('tontine.planStatus.PENDING_PARTNER_VERIFICATION'),
      SETUP_REQUIRED: t('tontine.planStatus.SETUP_REQUIRED'),
      DRAFT: t('tontine.planStatus.DRAFT'),
      READY_TO_ACTIVATE: t('tontine.planStatus.READY_TO_ACTIVATE'),
      PAUSED: t('tontine.planStatus.PAUSED'),
      COMPLETED: t('tontine.planStatus.COMPLETED'),
      CANCELLED: t('tontine.planStatus.CANCELLED'),
      ARCHIVED: t('tontine.planStatus.ARCHIVED'),
    }[s] || s
  )
})
const frequencyLabel = computed(() => ({
  WEEKLY: t('tontine.frequency.WEEKLY'),
  BIWEEKLY: t('tontine.frequency.BIWEEKLY'),
  MONTHLY: t('tontine.frequency.MONTHLY'),
  QUARTERLY: t('tontine.frequency.QUARTERLY'),
  CUSTOM: t('tontine.frequency.CUSTOM'),
}[plan.value?.frequency] || t('tontine.frequency.MONTHLY')))
const modeLabel = computed(() => ({
  COORDINATION_ONLY: t('tontine.mode.COORDINATION_ONLY'),
  PARTNER_PAYMENT_ORCHESTRATED: t('tontine.mode.PARTNER_PAYMENT_ORCHESTRATED'),
  REGULATED_PARTNER_SAVINGS: t('tontine.mode.REGULATED_PARTNER_SAVINGS'),
}[plan.value?.operatingMode] || t('tontine.mode.COORDINATION_ONLY')))
const allocationLabel = computed(() => ({
  FIXED_ROTATION: t('tontine.allocation.FIXED_ROTATION'),
  RANDOM_DRAW: t('tontine.allocation.RANDOM_DRAW'),
  MEMBER_VOTE: t('tontine.allocation.MEMBER_VOTE'),
  COMMITTEE_APPROVAL: t('tontine.allocation.COMMITTEE_APPROVAL'),
  NEED_BASED: t('tontine.allocation.NEED_BASED'),
}[plan.value?.allocationMethod] || ''))

const contributionStatusClass = computed(() => {
  const s = plan.value?.myContribution?.status
  return s === 'CONFIRMED' ? 'text-color-green' : s === 'OVERDUE' ? 'text-color-red' : 'text-color-orange'
})
const contributionStatusLabel = computed(() => {
  const s = plan.value?.myContribution?.status
  return (
    {
      DUE: t('tontine.contributionStatus.DUE'),
      PAYMENT_REQUESTED: t('tontine.contributionStatus.PAYMENT_REQUESTED'),
      PAYMENT_PENDING_CONFIRMATION: t('tontine.contributionStatus.PAYMENT_PENDING_CONFIRMATION'),
      RECEIPT_SUBMITTED: t('tontine.contributionStatus.RECEIPT_SUBMITTED'),
      CONFIRMED: t('tontine.contributionStatus.CONFIRMED'),
      OVERDUE: t('tontine.contributionStatus.OVERDUE'),
      NOT_DUE: t('tontine.contributionStatus.NOT_DUE'),
      EXCUSED: t('tontine.contributionStatus.EXCUSED'),
      WAIVED: t('tontine.contributionStatus.WAIVED'),
      REVERSED: t('tontine.contributionStatus.REVERSED'),
    }[s] || s
  )
})

onMounted(load)

async function load() {
  try {
    await store.loadSummary(props.campaignId)
  } catch (err) {
    const status = err.response?.status
    if (status === 404 || status === 400) {
      f7.views.main.router.navigate(
        `/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/setup/`
      )
      return
    }
    f7.dialog.alert(t('tontine.loadError'))
  }
}

async function payContribution() {
  const c = plan.value?.myContribution
  if (!c) return
  if (plan.value?.operatingMode === 'COORDINATION_ONLY') {
    navigateContribution(c.id)
    return
  }
  try {
    payment.triggerCampaignContribution({
      campaignId: props.campaignId,
      groupId: props.groupId,
      amount: c.expectedAmount,
      campaignType: 'TONTINE',
      currency: plan.value?.currency,
      campaignName: plan.value?.campaignName,
      description: t('tontine.contributionForCycle', { number: plan.value?.currentCycle?.cycleNumber || 1 }),
    })
  } catch (err) {
    f7.dialog.alert(err.message || t('tontine.payError'))
  }
}

function navigateContribution(contributionId) {
  const cycleId = plan.value?.currentCycle?.id
  f7.views.main.router.navigate(
    `/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/contributions/${contributionId}${cycleId ? `?cycleId=${cycleId}` : ''}`
  )
}

function openContribution() {
  if (plan.value?.myContribution?.id) navigateContribution(plan.value.myContribution.id)
}
function openCycle() {
  if (plan.value?.currentCycle?.id) {
    f7.views.main.router.navigate(
      `/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/cycles/${plan.value.currentCycle.id}`
    )
  }
}
function openRules() {
  f7.views.main.router.navigate(`/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/rules`)
}
function openLedger() {
  f7.views.main.router.navigate(`/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/ledger`)
}
function inviteMembers() {
  f7.views.main.router.navigate(`/groups/${props.groupId}/members`)
}
function continueSetup() {
  f7.views.main.router.navigate(`/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/setup/`)
}

async function generateCalendar() {
  f7.preloader.show()
  try {
    await store.generateCycles(props.campaignId)
    await load()
    f7.toast.show({ text: t('tontine.calendarGenerated'), position: 'bottom' })
  } catch (err) {
    f7.dialog.alert(err.response?.data?.message || err.message || t('tontine.generateError'))
  } finally {
    f7.preloader.hide()
  }
}

function money(v, c) {
  return new Intl.NumberFormat(keycloak.locale || 'fr', {
    style: 'currency',
    currency: c || 'XOF',
    maximumFractionDigits: 0,
  }).format(v || 0)
}
function formatDate(v) {
  return v
    ? new Intl.DateTimeFormat(keycloak.locale || 'fr', { day: 'numeric', month: 'long', year: 'numeric' }).format(
        new Date(v.startsWith('20') && v.length === 10 ? v + 'T00:00:00' : v)
      )
    : '—'
}
</script>

<style scoped>
.status-card h2 { margin: 12px 0 4px; font-size: 28px; }
.status-card p { color: var(--f7-on-surface-variant); margin: 0 0 14px; }
.status-row { display: flex; align-items: center; justify-content: space-between; }
.mode-pill { font-size: 12px; color: var(--f7-theme-color); font-weight: 600; }
.badge-green { background: #00b894; }
.badge-orange { background: #f0932b; }
.badge-gray { background: #9e9e9e; }
.progress-meta { display: flex; justify-content: space-between; font-size: 12px; margin-top: 6px; color: var(--f7-on-surface-variant); }
.no-cycle { display: flex; align-items: center; gap: 8px; margin: 8px 0 0; color: var(--f7-on-surface-variant); font-size: 13px; }
.setup-warning { display: flex; align-items: flex-start; gap: 10px; }
.setup-warning div { flex: 1; }
.setup-warning p { margin: 3px 0 0; font-size: 12px; color: var(--f7-on-surface-variant); }
.blockers { margin: 4px 0 0; padding-left: 16px; font-size: 12px; color: var(--f7-on-surface-variant); }
.my-contribution-title { font-size: 13px; color: var(--f7-on-surface-variant); }
.my-contribution-amount { font-size: 28px; font-weight: 700; margin: 4px 0; }
.info-notice p { font-size: 13px; line-height: 1.5; margin: 0; }
</style>